import React, { useState } from 'react';
import { Gift, Crown, Star, Heart, Zap, Award, Gem, TrendingUp, CheckCircle, Shield, Users} from 'lucide-react';

const Page5001000 = ({ setCurrentPage }) => {
  const [selectedType, setSelectedType] = useState('all');

  const luxuryTypes = [
    { id: 'premium', name: 'Premium produkter', icon: Crown, color: 'purple' },
    { id: 'memorable', name: 'Minnerike opplevelser', icon: Heart, color: 'red' },
    { id: 'investment', name: 'Investeringspiece', icon: Gem, color: 'blue' },
    { id: 'technology', name: 'High-end teknologi', icon: Zap, color: 'indigo' },
    { id: 'personal', name: 'Dypt personlige', icon: Star, color: 'yellow' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-indigo-50 to-primary-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👑</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver 500-1000 kr</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Premium-segmentet hvor kvalitet møter luksus. Her finner du gaver som gjør varig inntrykk 
              og viser hvor mye noen betyr for deg - fra eksklusive opplevelser til tidløse verdier.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn premium gaver
            </button>
          </div>
        </div>
      </section>

      {/* Premium Philosophy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Når gaven skal være noe ekstraordinært
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-100">
                <Crown className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Utsøkt kvalitet</h3>
                <p className="text-secondary-600">
                  I denne prisklassen handler det om eksepsjonell kvalitet, håndverk og materialær. 
                  Gaver som er laget for å vare og som stråler av premiumfølelse.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-primary-50 rounded-lg p-6 border border-indigo-100">
                <Heart className="w-12 h-12 text-indigo-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Dype relasjoner</h3>
                <p className="text-secondary-600">
                  Denne investeringen er for de som betyr mest - kjæreste, ektefelle, foreldre, 
                  nære familiemedlemmer eller ved spesielle milepæler i livet.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100">
                <Award className="w-12 h-12 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Spesielle anledninger</h3>
                <p className="text-secondary-600">
                  Milepæl-bursdager, jubileum, forlovelse, graduering eller andre øyeblikk 
                  som fortjener å markeres med noe virkelig spesielt.
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Gem className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">En investering i relasjoner</h3>
                  <p className="text-warning-700">
                    Gaver i denne prisklassen er ofte investeringer som varer i årevis, blir familiearvestykker 
                    eller skaper minner som huskes for alltid. Det handler ikke bare om prisen, 
                    men om den symbolske verdien og den varige påvirkningen gaven vil ha.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Premium gavekategorier
            </h2>

            {/* Type Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedType('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedType === 'all'
                    ? 'bg-purple-500 text-white border-purple-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-purple-300'
                }`}
              >
                Alle kategorier
              </button>
              {luxuryTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedType === type.id
                      ? `bg-${type.color}-500 text-white border-${type.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <type.icon className="w-4 h-4" />
                  <span>{type.name}</span>
                </button>
              ))}
            </div>

            {/* Category Cards */}
            <div className="space-y-8">
              {/* Premium Products */}
              <div className={`${selectedType === 'all' || selectedType === 'premium' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Crown className="w-10 h-10 text-purple-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Premium produkter</h3>
                      <p className="text-secondary-600">Eksepsjonell kvalitet som varer livet ut</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💍 Smykker og tilbehør (500-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Kvalitets sølv- og gullsmykker</li>
                        <li>• Designerklokker og armbånd</li>
                        <li>• Ækte lær vesker og lommebøker</li>
                        <li>• Personaliserte og graverte smykker</li>
                        <li>• Vintage og antikviteter</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏠 Luksus for hjemmet (600-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Designermøbler og interiør</li>
                        <li>• Håndlaget keramikk og kunstglass</li>
                        <li>• Premium tekstiler og sengetøy</li>
                        <li>• Eksklusive duftlys og aromaterapi</li>
                        <li>• Kunsthåndverk og samlergjenstander</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">👔 Livsstil og skjønnhet (500-900 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Luksus hudpleie og skjønnhet</li>
                        <li>• Eksklusive parfymer og duftsamlinger</li>
                        <li>• Premium klær og tilbehør</li>
                        <li>• Kvalitets barbering- og groomingutstyr</li>
                        <li>• Spa og wellness-pakker</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Memorable Experiences */}
              <div className={`${selectedType === 'all' || selectedType === 'memorable' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Heart className="w-10 h-10 text-error-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Minnerike opplevelser</h3>
                      <p className="text-secondary-600">Opplevelser som skaper minner for livet</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border border-error-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">✈️ Reise og eventyr (600-1000 kr)</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Romantiske weekendturer</li>
                          <li>• Spa og wellness-opphold</li>
                          <li>• Kulinariske opplevelser og gourmet-turer</li>
                          <li>• Naturopplevelser og aktivitetsferier</li>
                          <li>• Kulturturer og historiske opplevelser</li>
                        </ul>
                      </div>
                      
                      <div className="border border-error-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">🎭 Kultur og underholdning (500-800 kr)</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Premium konsert- og teaterbilletter</li>
                          <li>• Eksklusiv kunstutstilling eller galleriaccess</li>
                          <li>• VIP-opplevelser og meet & greet</li>
                          <li>• Festival og event-pakker</li>
                          <li>• Private guideturer og masterclasses</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                      <h4 className="font-semibold text-error-800 mb-3">💫 Hvorfor opplevelser er verdifulle</h4>
                      <div className="text-sm text-error-700 space-y-3">
                        <p><strong>Varige minner:</strong> Opplevelser skaper minner som ofte verdsettes mer enn fysiske gjenstander over tid.</p>
                        <p><strong>Delt glede:</strong> Mange opplevelser kan deles med andre, noe som forsterker relasjoner og skaper felles minner.</p>
                        <p><strong>Personlig vekst:</strong> Nye opplevelser kan åpne nye horisonter og bidra til personlig utvikling.</p>
                        <p><strong>Ingen oppbevaring:</strong> Opplevelser tar ikke opp plass og blir aldri "rot" eller utdaterte.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Pieces */}
              <div className={`${selectedType === 'all' || selectedType === 'investment' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Gem className="w-10 h-10 text-accent-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Investeringspiece</h3>
                      <p className="text-secondary-600">Gaver som øker i verdi og betydning over tid</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">⌚ Tidløse klassikere (700-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Kvalitetsklokker som varer generasjoner</li>
                        <li>• Klassiske smykker i edle materialer</li>
                        <li>• Håndverk fra anerkjente kunstnere</li>
                        <li>• Vintage og antikke gjenstander</li>
                        <li>• Førsteutgave bøker og samlerobjekter</li>
                      </ul>
                    </div>
                    
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎨 Kunst og kultur (500-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Originale kunstverker</li>
                        <li>• Signerte trykk og litografi</li>
                        <li>• Håndlagde keramikk og skulpturer</li>
                        <li>• Begrenset opplag designobjekter</li>
                        <li>• Kulturelle arvesting og håndverk</li>
                      </ul>
                    </div>
                    
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📚 Læring og utvikling (600-900 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Eksklusive kurs og workshops</li>
                        <li>• Mentoring og coaching-sesjoner</li>
                        <li>• Profesjonell sertifisering</li>
                        <li>• Medlemskap i eksklusive organisasjoner</li>
                        <li>• Språkkurs eller ferdighetsutvikling</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* High-end Technology */}
              <div className={`${selectedType === 'all' || selectedType === 'technology' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Zap className="w-10 h-10 text-indigo-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">High-end teknologi</h3>
                      <p className="text-secondary-600">Premium tech som gjør en forskjell</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📱 Cutting-edge gadgets (500-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Premium smartklokker og fitness-trackere</li>
                        <li>• High-end hodetelefoner og audio-utstyr</li>
                        <li>• Professional-grade kamerautstyr</li>
                        <li>• Smart home premium-systemer</li>
                        <li>• VR/AR utstyr og gaming-teknologi</li>
                      </ul>
                    </div>
                    
                    <div className="border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💻 Profesjonelt utstyr (600-1000 kr)</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Ergonomisk premium workspace-utstyr</li>
                        <li>• Professional software og lisenser</li>
                        <li>• High-end tilbehør og peripherals</li>
                        <li>• Sikkerhet og backup-løsninger</li>
                        <li>• Målrettet tech for deres bransje</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-800 mb-2">⚡ Tech-investering tips</h4>
                    <p className="text-indigo-700 text-sm">
                      I denne prisklassen får du premium-teknologi som ofte har bedre bygging, lengre levetid, 
                      bedre support og funksjoner som skiller seg ut. Research grundig og velg merker 
                      kjent for kvalitet og innovasjon.
                    </p>
                  </div>
                </div>
              </div>

              {/* Deeply Personal */}
              <div className={`${selectedType === 'all' || selectedType === 'personal' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Star className="w-10 h-10 text-warning-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Dypt personlige gaver</h3>
                      <p className="text-secondary-600">Tilpasset deres unike historie og drømmer</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border border-warning-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">💝 Skreddersydde gaver (500-900 kr)</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Tilpassede kunstverker og portretter</li>
                          <li>• Personaliserte smykker med gravering</li>
                          <li>• Skreddersydde opplevelser</li>
                          <li>• Håndlaget tilpasset designobjekter</li>
                          <li>• Genealogi og slektshistorie research</li>
                        </ul>
                      </div>
                      
                      <div className="border border-warning-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">📖 Minner og arv (600-1000 kr)</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Profesjonelle familiefotosesjoner</li>
                          <li>• Eksklusive fotobøker og albumsdesign</li>
                          <li>• Personlige historieopptak og dokumentarer</li>
                          <li>• Støtte deres lidenskap eller drøm</li>
                          <li>• Bidrag til deres mål eller prosjekt</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                      <h4 className="font-semibold text-warning-800 mb-3">⭐ Slik skaper du en virkelig personlig gave</h4>
                      <div className="text-sm text-warning-700 space-y-3">
                        <p><strong>Lytt til deres drømmer:</strong> Hva har de alltid ønsket å gjøre, lære eller oppleve?</p>
                        <p><strong>Observer deres utfordringer:</strong> Hva sliter de med som du kan hjelpe å løse?</p>
                        <p><strong>Feir deres prestasjoner:</strong> Anerkjenn noe spesielt de har oppnådd.</p>
                        <p><strong>Støtt deres vekst:</strong> Hjelp dem å bli den de ønsker å være.</p>
                        <p><strong>Skape historier:</strong> Knytt gaven til deres personlige historie eller familie.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions and Relationships */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Når 500-1000 kr gaver er riktige valget
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-100">
                  <h3 className="text-xl font-semibold text-secondary-800 mb-4">🎯 Perfekte anledninger</h3>
                  <ul className="space-y-2 text-secondary-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Milepæl-bursdager:</strong> 30, 40, 50, 60+ år</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Jubileum:</strong> 5, 10, 25+ års ekteskap</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Livshendelser:</strong> Graduering, forfremmelse, pensjonering</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Familietradisjon:</strong> Konfirmasjon, 18-årsdag</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Spesielle takk:</strong> Enorm tjeneste eller støtte</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-primary-50 rounded-lg p-6 border border-indigo-100">
                  <h3 className="text-xl font-semibold text-secondary-800 mb-4">💕 Relasjonsnivå</h3>
                  <ul className="space-y-2 text-secondary-600">
                    <li className="flex items-start">
                      <Users className="w-5 h-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Ektefelle/Partner:</strong> Hovedgave til store anledninger</span>
                    </li>
                    <li className="flex items-start">
                      <Users className="w-5 h-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Foreldre/Barn:</strong> Milepæler og spesielle øyeblikk</span>
                    </li>
                    <li className="flex items-start">
                      <Users className="w-5 h-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Nære venner:</strong> Livshendelser og jubileer</span>
                    </li>
                    <li className="flex items-start">
                      <Users className="w-5 h-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Familie fellesgave:</strong> Samlet fra flere for større anledning</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                  <Shield className="w-8 h-8 text-success-500 mb-4" />
                  <h3 className="text-xl font-semibold text-success-800 mb-4">Smart kjøpsstrategi</h3>
                  <ul className="space-y-2 text-success-700 text-sm">
                    <li>• Planlegg i god tid - premium-produkter kan ha leveringstid</li>
                    <li>• Research grundig - les anmeldelser og sammenlign</li>
                    <li>• Vurder garantier og service for dyre produkter</li>
                    <li>• Sjekk returpolicy - viktig ved større investeringer</li>
                    <li>• Vurder personalisering som øker verdi</li>
                    <li>• Tenk langsiktig - vil dette være verdifullt om 5-10 år?</li>
                  </ul>
                </div>

                <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
                  <TrendingUp className="w-8 h-8 text-warning-500 mb-4" />
                  <h3 className="text-xl font-semibold text-warning-800 mb-4">Maksimer verdien</h3>
                  <ul className="space-y-2 text-warning-700 text-sm">
                    <li>• Kombiner med en personlig opplevelse</li>
                    <li>• Invester i kvalitets-innpakning og presentasjon</li>
                    <li>• Dokumenter øyeblikket gaven gis</li>
                    <li>• Følg opp med vedlikehold eller service</li>
                    <li>• Fortell historien bak gavevalgetet</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Considerations */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tenk som en gaveinvestor
            </h2>

            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <Gem className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Verdi over tid</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">📈 Økende verdi</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitetssmykker og klokker</li>
                      <li>• Kunsthåndverk og begrenset opplag</li>
                      <li>• Vintage og antikke gjenstander</li>
                      <li>• Samlergjenstander og memorabilia</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">🔄 Vedvarende nytte</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitetsverktøy og utstyr</li>
                      <li>• Møbler og interiør i tidløst design</li>
                      <li>• Teknologi med lang levetid</li>
                      <li>• Utdanning og ferdighetsutvikling</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">💝 Emosjonell verdi</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Opplevelser som skaper minner</li>
                      <li>• Personaliserte og tilpassede gaver</li>
                      <li>• Familiearvestykker og tradisjoner</li>
                      <li>• Støtte til deres drømmer og mål</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <Star className="w-8 h-8 text-warning-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Kvalitetsindikatorer</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">✅ Velg produkter med:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Etablerte merker med god historie</li>
                      <li>• Høykvalitets materialer og konstruksjon</li>
                      <li>• God garanti og servicedekning</li>
                      <li>• Positive anmeldelser over tid</li>
                      <li>• Tidløst design som ikke går av moten</li>
                      <li>• Mulighet for reparasjon og vedlikehold</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">❌ Unngå produkter som:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Er laget for å være trend-baserte</li>
                      <li>• Har dårlige materialqualitet</li>
                      <li>• Mangler service og støtte</li>
                      <li>• Er kopier eller imitasjoner</li>
                      <li>• Har komplisert vedlikehold</li>
                      <li>• Er produsert under dårlige forhold</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <Crown className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-purple-800 mb-2">Den ultimate premium-gaven</h3>
                    <p className="text-purple-700">
                      Den perfekte gaven i denne prisklassen kombinerer eksepsjonell kvalitet med dyp personlig betydning. 
                      Det kan være en vakker klokke graviert med en betydningsfull dato, en opplevelse som oppfyller en 
                      langvarig drøm, eller et kunsthåndverk som representerer deres personlighet. 
                      Nøkkelen er balansen mellom luksus og mening.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-500 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å investere i en virkelig spesiell gave?
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Bruk vår gavefinner for å oppdage premium-gaver som skaper varige minner og viser hvor mye noen betyr
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('price')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-purple-600 transition-colors font-semibold"
              >
                Se andre prisklasser
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Page5001000;